import Header from '@/components/common/MainContentHeader';
import useI18n from '../hooks/useI18n';
import { UserContext } from '@/context/UserContext';
import { useContext, useEffect, useState } from 'react';
import MultiStepForm from '@/components/common/MultiStepForm';
import { useParams, useNavigate } from 'react-router-dom';
import { Campaign as CampaignType } from '@/interfaces/campaign';
import Loading from '@/components/common/Loading';
import ActiveCampaignRunningModal from '@/components/Modal/ActiveCampaignRunningModal';
import {
  useGetCampaignByIdQuery,
  useGetCampaignStatsQuery,
} from '@/hooks/queries/useCampaignQueries';

const EditCampaign: React.FC = () => {
  const { t } = useI18n();
  const { id } = useParams();
  const navigate = useNavigate();
  const userContext = useContext(UserContext);
  const [currentStep, setCurrentStep] = useState(1);
  const [campaign, setCampaign] = useState<CampaignType>();

  const [campaignBlock, setCampaignBlock] = useState<{
    title: string;
    message: string;
    redirect: string;
  } | null>(null);

  if (!userContext) {
    throw new Error('useContext must be used within a UserProvider');
  }
  const { user } = userContext;

  const brandId = user?.brand?._id || '';

  const { data: campaignStats } = useGetCampaignStatsQuery(brandId, !!brandId);

  const { data: campaignData, isLoading } = useGetCampaignByIdQuery(
    id || '',
    !!id && !!campaignStats,
  );

  useEffect(() => {
    if (!campaignData?.campaign || !campaignStats) return;

    const fetchedCampaign = campaignData.campaign;
    const isDraft = fetchedCampaign.status === 'draft';

    if (isDraft) {
      const hasActiveCampaign =
        campaignStats.live > 0 ||
        campaignStats.paused > 0 ||
        campaignStats.scheduled > 0;

      if (hasActiveCampaign) {
        setCampaignBlock({
          title: 'Draft Campaign Update',
          message:
            'To modify or launch this draft, please wait for your current campaign to end.',
          redirect: '/active-campaign',
        });
        return;
      }
    }
    setCampaign(fetchedCampaign);
  }, [campaignData, campaignStats]);

  const getSubtitle = () => {
    if (currentStep === 6) {
      return t('common.previewCampaign');
    }
    return t('common.editCampaign');
  };

  const getTitle = () => {
    if (user && user.brand) {
      return `${user.brand.name}'s`;
    }
    return '';
  };

  return (
    <>
      {campaignBlock && (
        <ActiveCampaignRunningModal
          isOpen={true}
          title={campaignBlock.title}
          message={campaignBlock.message}
          onClose={() => {
            setCampaignBlock(null);
            navigate(campaignBlock.redirect, {
              state: { tab: 'draft' },
              replace: true,
            });
          }}
        />
      )}
      <div className="h-full flex flex-col">
        <div className="p-[24px] lg:bg-[#F0F5FF] bg-white shrink-0">
          <Header title={getTitle()} subtitle={getSubtitle()} />
        </div>
        <div
          className={`bg-white p-[12px] sm:p-[24px] flex-1 min-h-0 flex flex-col ${
            currentStep === 6 ? 'pt-0 sm:pt-0' : ''
          }`}
        >
          {isLoading || !campaign ? (
            <Loading
              centeredToParent={true}
              style={{ top: 'calc(50% + 60px)' }}
            />
          ) : (
            <MultiStepForm
              campaign={campaign}
              isEditCampaign={true}
              onStepChange={setCurrentStep}
            />
          )}
        </div>
      </div>
    </>
  );
};

export default EditCampaign;
